<script setup lang="ts">
  import { type HTMLAttributes, computed } from 'vue'
  import {
    DialogClose,
    DialogContent,
    type DialogContentEmits,
    type DialogContentProps,
    DialogOverlay,
    DialogPortal,
    useForwardPropsEmits
  } from 'radix-vue'
  import { X } from 'lucide-vue-next'
  import { cn } from '@/lib/utils'

  const props = defineProps<
    DialogContentProps & {
      class?: HTMLAttributes['class']
      overlayClass?: HTMLAttributes['class']
    }
  >()
  const emits = defineEmits<DialogContentEmits>()

  const delegatedProps = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { class: _, overlayClass: __, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="
        cn(
          `fixed inset-0 z-[56] grid place-items-center overflow-y-auto bg-black/80
          data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0
          data-[state=open]:fade-in-0`,
          props.overlayClass
        )
      "
    >
      <DialogContent
        v-bind="forwarded"
        :class="
          cn(
            `relative z-[56] my-8 grid w-full max-w-lg gap-4 border border-border bg-background p-6 shadow-lg
            duration-200 sm:rounded-lg md:w-full`,
            props.class
          )
        "
        @pointer-down-outside="
          (event) => {
            const originalEvent = event.detail.originalEvent
            const target = originalEvent.target as HTMLElement
            if (
              originalEvent.offsetX > target.clientWidth ||
              originalEvent.offsetY > target.clientHeight
            ) {
              event.preventDefault()
            }
          }
        "
      >
        <slot />

        <DialogClose
          class="absolute right-3 top-3 rounded-md p-0.5 transition-colors hover:bg-secondary"
        >
          <X class="h-4 w-4" />
          <span class="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </DialogOverlay>
  </DialogPortal>
</template>
